// client/src/coms/e2ee/DmEncryptionBar.js
// The encryption line under a DM's header, the acknowledgement that pauses sending after a downgrade, safety-code
// verification and the review of a contact's identity change. It never pops up: the status is always visible in
// place, and the warning for "the other participant has encryption disabled" stays in the header for as long as
// it applies.
import React, { useState } from 'react';
import { requestDeviceVerification } from './cryptoSession';
import { describeDmEncryption } from './dmStatus';
import './e2ee.css';

function openPrivacySettings() {
  try {
    window.__voxPendingSettingsTab = 'privacy';
    window.dispatchEvent(new CustomEvent('vox-open-settings', { detail: { tab: 'privacy' } }));
  } catch {}
}

// contact: { verified, mine: { fingerprint }, theirs: { fingerprint } } (safety codes of both identities)
export default function DmEncryptionBar({ e2ee, local, contact, otherName, onAcknowledge, onRetry, onVerify, onAcceptIdentity }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [panel, setPanel] = useState(''); // '' | 'verify' | 'identity'
  const d = describeDmEncryption(e2ee, { otherName, local, contact });
  const pending = e2ee && e2ee.pending_ack ? e2ee.pending_ack : null;
  const who = String(otherName || '').trim() || 'the other participant';
  // Warnings are always shown in full; the other states show a short label that expands.
  const showText = open || d.tone === 'warning';

  const run = async (fn) => {
    if (busy || typeof fn !== 'function') return;
    setBusy(true);
    setError('');
    try {
      await fn();
      setPanel('');
    } catch (e) {
      setError(String((e && e.message) || e));
    } finally {
      setBusy(false);
    }
  };

  const codes = contact && contact.theirs ? (
    <div className="vx-e2ee-codes">
      <span>
        {who}’s safety code: <code data-testid="dm-e2ee-their-code">{contact.theirs.fingerprint || '—'}</code>
      </span>
      <span>
        Your safety code: <code>{(contact.mine && contact.mine.fingerprint) || '—'}</code>
      </span>
    </div>
  ) : null;

  return (
    <div className="vx-e2ee-wrap" data-testid="dm-e2ee">
      <div className={`vx-e2ee-bar is-${d.tone}`} data-e2ee-tone={d.tone} data-e2ee-reason={(e2ee && e2ee.reason) || ''} data-e2ee-verified={contact && contact.verified ? '1' : '0'}>
        <span className="vx-e2ee-icon" aria-hidden="true">{d.icon}</span>
        <button type="button" className="vx-e2ee-label" aria-expanded={showText} onClick={() => setOpen((v) => !v)} title="Encryption status of this conversation">
          {d.label}
        </button>
        {showText ? (
          <span className="vx-e2ee-text" data-testid="dm-e2ee-text">
            {d.text}{' '}
            {d.retry && typeof onRetry === 'function' ? (
              <button type="button" className="vx-e2ee-link" onClick={onRetry} data-testid="dm-e2ee-retry">
                Try again
              </button>
            ) : null}{' '}
            {d.action === 'verify_device' ? (
              <button type="button" className="vx-e2ee-link" onClick={() => requestDeviceVerification()} data-testid="dm-e2ee-verify-device">
                Verify this device
              </button>
            ) : (
              <button type="button" className="vx-e2ee-link" onClick={openPrivacySettings}>
                Encryption settings
              </button>
            )}
          </span>
        ) : null}
        {d.action === 'verify' && typeof onVerify === 'function' ? (
          <button type="button" className="vx-e2ee-link" onClick={() => setPanel(panel === 'verify' ? '' : 'verify')} data-testid="dm-e2ee-verify">
            Verify {who}
          </button>
        ) : null}
        {d.action === 'accept_identity' && typeof onAcceptIdentity === 'function' ? (
          <button type="button" className="vx-e2ee-link" onClick={() => setPanel(panel === 'identity' ? '' : 'identity')} data-testid="dm-e2ee-review">
            Review change
          </button>
        ) : null}
      </div>

      {panel === 'verify' ? (
        <div className="vx-e2ee-confirm" data-testid="dm-e2ee-verify-panel">
          <strong>Verify {who}</strong>
          <span>
            Compare these safety codes with {who} in person, on a call, or another channel you trust (not this chat). Only mark them verified if both codes
            match exactly what {who} sees.
          </span>
          {codes}
          <div className="vx-e2ee-confirm-row">
            <button type="button" className="vx-btn small" disabled={busy} onClick={() => run(onVerify)} data-testid="dm-e2ee-verify-confirm">
              {busy ? 'Saving…' : 'The codes match: mark verified'}
            </button>
            <button type="button" className="vx-btn small" disabled={busy} onClick={() => setPanel('')}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {panel === 'identity' ? (
        <div className="vx-e2ee-ack" data-testid="dm-e2ee-identity-panel">
          <strong>{who}’s encryption identity changed</strong>
          <span>
            Ask {who} (outside this chat) whether they reset their encryption. If you accept the new identity, new messages will be encrypted to it; if you had
            verified {who}, that verification is withdrawn and you should verify again.
          </span>
          {codes}
          <div className="vx-e2ee-ack-row">
            <button type="button" className="vx-btn small" disabled={busy} onClick={() => run(onAcceptIdentity)} data-testid="dm-e2ee-accept-identity">
              {busy ? 'Saving…' : 'Accept the new identity'}
            </button>
            <button type="button" className="vx-btn small" disabled={busy} onClick={() => setPanel('')}>
              Not now
            </button>
          </div>
        </div>
      ) : null}
      {error ? <span className="vx-e2ee-error">{error}</span> : null}

      {pending ? (
        <div className="vx-e2ee-ack" role="alert" data-testid="dm-e2ee-ack">
          <strong>Encryption changed in this conversation.</strong>
          <span>{pending.content}</span>
          <span>Sending is paused until you confirm you understand that new messages will be standard (not end-to-end encrypted).</span>
          <div className="vx-e2ee-ack-row">
            <button type="button" className="vx-btn small" onClick={() => run(() => onAcknowledge(pending.message_id))} disabled={busy} data-testid="dm-e2ee-ack-btn">
              {busy ? 'Saving…' : 'Acknowledge'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
